import { tzOffsetMinutesAt } from '../scheduleTime.js';
import { gradeCallByOutcome, type CallGrade } from '../leads/callGrade.js';
import { callResultForChain } from '../leads/callResult.js';
import type { LeadCall } from '../../types/leadCall.js';

// THE SHARED KYCKCONNECT METRICS (Phase 3: the portal Dashboard and Reporting, and the staff Reporting). PURE: no clock, no I/O.
// One definition of every figure's building block, so the dashboard and the reports cannot disagree about "reached", "speed to lead" or
// "a booked call".
//
// ⚠️ HONESTY: a ratio of nothing is NULL, never 0%. A series bucket with no calls has a NULL speed, not a zero (a gap, not a fast
// response). A bucket with no leads IS a real zero (it is our own data, so a quiet day is a quiet day).

export type Grouping = 'day' | 'week' | 'month';
export const GROUPINGS: readonly Grouping[] = ['day', 'week', 'month'];

export const DEFAULT_TIMEZONE = 'Australia/Brisbane';

/** `Intl` throws on a zone it does not know. A stored zone that is junk must not take a report down: it reads as the default. */
export function safeTimezone(tz: string | null | undefined): string {
  if (!tz) return DEFAULT_TIMEZONE;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

export interface LocalParts {
  y: number;
  /** 1 to 12. */
  m: number;
  d: number;
  hour: number;
  /** 0 = Sunday ... 6 = Saturday. */
  dow: number;
}

/** The wall-clock parts of an instant in a zone (DST-correct: the offset is read AT that instant). */
export function localParts(ms: number, tz: string): LocalParts {
  const shifted = new Date(ms + tzOffsetMinutesAt(ms, tz) * 60_000);
  return { y: shifted.getUTCFullYear(), m: shifted.getUTCMonth() + 1, d: shifted.getUTCDate(), hour: shifted.getUTCHours(), dow: shifted.getUTCDay() };
}

const pad = (n: number): string => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number): string => `${y}-${pad(m)}-${pad(d)}`;

/** Pure calendar arithmetic on a `YYYY-MM-DD` (no instants, so no DST). */
export function addDays(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** The MONDAY of the week containing a local date (weeks start on Monday). */
export function weekStart(key: string): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return addDays(key, -((dow + 6) % 7));
}

/** The bucket a local instant falls in: `YYYY-MM-DD` (day), the Monday `YYYY-MM-DD` (week) or `YYYY-MM` (month). */
export function bucketKeyOf(ms: number, grouping: Grouping, tz: string): string {
  const p = localParts(ms, tz);
  const day = ymd(p.y, p.m, p.d);
  if (grouping === 'day') return day;
  if (grouping === 'week') return weekStart(day);
  return `${p.y}-${pad(p.m)}`;
}

/** Every bucket from the one holding `fromMs` to the one holding `toMs`, in order (the zero-fill domain of a chart). */
export function bucketKeysBetween(fromMs: number, toMs: number, grouping: Grouping, tz: string): string[] {
  if (toMs < fromMs) return [];
  const first = bucketKeyOf(fromMs, grouping, tz);
  const last = bucketKeyOf(toMs, grouping, tz);
  const keys: string[] = [];
  if (grouping === 'month') {
    let [y, m] = first.split('-').map(Number) as [number, number];
    const [ly, lm] = last.split('-').map(Number) as [number, number];
    while (y < ly || (y === ly && m <= lm)) {
      keys.push(`${y}-${pad(m)}`);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return keys;
  }
  const step = grouping === 'day' ? 1 : 7;
  for (let k = first; k <= last && keys.length < 5000; k = addDays(k, step)) keys.push(k);
  return keys;
}

export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid]! : Math.round((s[mid - 1]! + s[mid]!) / 2);
}

/** A percentage 0 to 100 (one decimal), or NULL when there is nothing to divide. */
export function pct(n: number, d: number): number | null {
  return d > 0 ? Math.round((n / d) * 1000) / 10 : null;
}

// ---- calls ----------------------------------------------------------------------------------------------------------------------

/** The slice of an outbound chain every figure reads. */
export type CallFacts = Pick<
  LeadCall,
  'state' | 'leadReceivedAt' | 'enquiryReceivedAt' | 'leadDialStartedAt' | 'leadReachedAt' | 'screenedOrMachine' | 'legs' | 'callOutcome' | 'summary' | 'answeredBy'
>;

/** THE HEADLINE: seconds from the lead arriving to the lead being dialled. Null when never dialled, or when the times read negative. */
export function callSpeedSeconds(c: Pick<CallFacts, 'leadReceivedAt' | 'leadDialStartedAt'> & Partial<Pick<CallFacts, 'enquiryReceivedAt'>> & Partial<Pick<LeadCall, 'kind'>>): number | null {
  // A call a person started (Leads centre v2, Part B) has no speed and never counts in any speed figure.
  if (c.kind === 'manual' || !c.leadDialStartedAt) return null;
  const s = Math.round((Date.parse(c.leadDialStartedAt) - Date.parse(c.enquiryReceivedAt ?? c.leadReceivedAt)) / 1000);
  return Number.isFinite(s) && s >= 0 ? s : null;
}

/**
 * THE AI SMS SPEED: seconds from the LEAD ARRIVING to the first text going out. ⚠️ NOT from the conversation's creation: a conversation
 * is created when its first text is sent, so that gap is always ~0 (the dashboard read "0s"). Null when the lead's arrival is unknown,
 * nothing was sent, or the times read negative.
 */
export function firstTextSeconds(leadReceivedAt: string | null | undefined, firstOutboundAt: string | null | undefined): number | null {
  if (!leadReceivedAt || !firstOutboundAt) return null;
  const s = Math.round((Date.parse(firstOutboundAt) - Date.parse(leadReceivedAt)) / 1000);
  return Number.isFinite(s) && s >= 0 ? s : null;
}

/**
 * The text speed for one conversation. ⚠️ It measures from the ENQUIRY that started the conversation (`enquiryReceivedAt`), never from
 * the lead's first-ever receipt unless the conversation predates that field AND its first text went out within an hour of it (else no
 * figure: a lead who enquired again a day later must not read as a day-long response).
 */
export function conversationTextSeconds(c: { enquiryReceivedAt?: string | null; createdAt: string; firstOutboundAt?: string | null }, leadReceivedAt: string | null | undefined): number | null {
  if (c.enquiryReceivedAt) return firstTextSeconds(c.enquiryReceivedAt, c.firstOutboundAt);
  const s = firstTextSeconds(leadReceivedAt ?? c.createdAt, c.firstOutboundAt);
  return s !== null && s <= 3600 ? s : null;
}

/**
 * THE REACHED FIGURE, in one place. ⚠️ A lead that arrived out of hours was NEVER CALLED, so it is reported on its own and is in neither
 * the reached count nor its denominator (a call nobody was asked to make cannot have failed to connect). A call still in progress has no
 * result yet and is not in the denominator either. Anything else that finished is one call: reached when a human answered.
 */
export function reachedFigures(calls: readonly Pick<CallFacts, 'state' | 'leadReachedAt' | 'screenedOrMachine'>[]): { outOfHours: number; calls: number; reached: number; reachedPct: number | null } {
  let outOfHours = 0;
  let decided = 0;
  let reached = 0;
  for (const c of calls) {
    const r = callResultForChain(c);
    if (r === null) continue;
    if (r.code === 'out_of_hours') {
      outOfHours += 1;
      continue;
    }
    decided += 1;
    if (callReached(c)) reached += 1;
  }
  return { outOfHours, calls: decided, reached, reachedPct: pct(reached, decided) };
}

/** A human reached the lead (the same rule the call result uses). */
export const callReached = (c: Pick<CallFacts, 'leadReachedAt'>): boolean => Boolean(c.leadReachedAt);

export interface GradedCall {
  grade: CallGrade;
  /** True when a person (or the system) set the call's outcome; false when the grade rests on the AI's suggestion alone. */
  confirmed: boolean;
}

/**
 * THE CALL'S QUALITY, GRADED BY OUTCOME AND NEVER BY DURATION (Scott, 2026-10-25), and only for a CONNECTED call (a call nobody picked up
 * has no quality). The outcome is the call's own (person-confirmed or system) when it has one, else the AI's SUGGESTION from a ready
 * summary, marked unconfirmed so the page can say how much of the picture is still the AI's reading. No outcome at all: no grade.
 * ⚠️ `gradeCallByOutcome` is the ONLY place an outcome becomes a grade.
 */
export function gradedCall(c: CallFacts): GradedCall | null {
  if (callResultForChain(c)?.code !== 'connected') return null;
  if (c.callOutcome) {
    const grade = gradeCallByOutcome(c.callOutcome.outcome);
    return grade ? { grade, confirmed: true } : null;
  }
  const suggested = c.summary?.status === 'ready' ? c.summary.suggestedOutcome : null;
  if (suggested) {
    const grade = gradeCallByOutcome(suggested);
    return grade ? { grade, confirmed: false } : null;
  }
  return null;
}

/**
 * Round-one staff answer: was a staff leg rung in round one, and did one of them take the call. ⚠️ A leg that took the call ends as
 * `completed` when the chain closes (the engine closes every unfinished leg then), so `accepted` alone would forget it; but a
 * `completed` leg that never rang through was also swept closed. A leg took the call when it is `accepted`, or `completed` with an
 * answer time.
 */
export function staffRoundOne(c: Pick<CallFacts, 'legs'>): { rung: boolean; answered: boolean } {
  const legs = (c.legs ?? []).filter((l) => l.role === 'staff' && l.round === 1);
  return { rung: legs.length > 0, answered: legs.some((l) => l.status === 'accepted' || (l.status === 'completed' && l.answeredAt !== null)) };
}

/** The staff member who answered (their team id), for the by-staff report. Null when nobody did. */
export const callAnsweredBy = (c: Pick<CallFacts, 'answeredBy'>): string | null => c.answeredBy ?? null;
